


















































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import { xLessonWebservice } from '@/webservices/xlesson';
import type { XProcess } from '@/models/xprocess';
import type { XSubProcess } from '@/models/xsubprocess';
import type { XLesson } from '@/models/xlesson';
import type { User } from '@/models/user';
import StoreUpdateXLessonModal from '@/components/knowledge/x/lessons/StoreUpdateXLessonModal.vue';
import SearchField from '@/components/prine/SearchField.vue';
import type { Company } from '@/models/company';
import { PERMISSIONS } from '@/helpers/auth/permissions';
import { hasAnyPermission, hasPermission } from '@/helpers/auth/permissionHelpers';
import { groupedXLessonPermissions } from '@/helpers/auth/commonPermissions';
import type { Tag } from '@/models/tag';
import type { Lesson } from '@/models/lesson';
import TagSelect from '@/components/form/TagSelect.vue';

@Component({
    name: 'XProcessesList',
    methods: { hasAnyPermission, hasPermission },
    computed: {
        PERMISSIONS() {
            return PERMISSIONS;
        },
    },
    components: {
        TagSelect,
        StoreUpdateXLessonModal,
        SearchField,
    },
})
export default class XProcessesList extends Vue {
    private filteredPhases = new Array<XProcess>();
    private subprocesses = new Array<XSubProcess>();
    private isStoreUpdateLessonModalActive: boolean = false;
    private lessonsCount: number = 0;
    private personalLessonsCount: number = 0;

    private tableSettings = {
        openedDetailed: Array<number>(),
        isStriped: true,
        isHoverable: true,
        isPaginated: false,
    };

    private selectedPhase: XProcess = {} as XProcess;
    private selectedSubPhase: XSubProcess = {} as XSubProcess;
    private selectedLesson: XLesson = {} as XLesson;
    private isSearching: boolean = false;

    get selectedTag(): Tag | null {
        return this.$store.getters['knowledge/xSelectedTag'];
    }

    set selectedTag(value: Tag | null) {
        this.$store.commit('knowledge/setXSelectedTag', value);
    }

    @Getter('knowledge/xSearchString')
    private searchQuery: string;
    private filterByLessons: boolean = hasPermission(PERMISSIONS.APPEX_X_LESSON);

    @Prop() private originalProcesses: XProcess[];

    @Getter('account/userInfo') private userInfo: User;
    @Getter('account/company') private company: Company;
    @Getter('utils/currentLocale') private currentLocale: string;

    @Watch('originalProcesses')
    private onOriginalProcessesChange() {
        this.prepareData();
    }

    @Watch('searchQuery')
    private onSearchQueryValueChange() {
        this.search();
    }

    @Watch('selectedTag')
    private onSelectedTagChange() {
        this.search();
    }

    private mounted() {
        this.prepareData();
        this.getLessonsCount();
        this.getPersonalLessonsCount();
    }

    private updateSearchQuery(value: string) {
        this.$store.commit('knowledge/setXSearchString', value);
    }

    private search() {
        this.isSearching = true;

        const regExQuery = new RegExp(this.searchQuery.trim(), 'i');

        let originalProcessesCopy: XProcess[];
        originalProcessesCopy = JSON.parse(JSON.stringify(this.originalProcesses));

        this.filteredPhases = originalProcessesCopy
            .filter((process: XProcess) => {
                let totalLessonsCount: number = 0;
                const processTitleMatch = process.title.search(regExQuery) !== -1;
                if (this.filterByLessons) {
                    const filteredSubProcesses = process.x_sub_processes.filter((subProcess: XSubProcess) => {
                        const filteredLessons = subProcess.x_lessons.filter((lesson: XLesson) => {
                            if (lesson.more_info) {
                                return (
                                    lesson.insight.search(regExQuery) !== -1 ||
                                    lesson.more_info.search(regExQuery) !== -1
                                );
                            } else {
                                return lesson.insight.search(regExQuery) !== -1;
                            }
                        });

                        subProcess.x_lessons = filteredLessons;
                        subProcess.x_lessons_count = filteredLessons.length;
                        totalLessonsCount += filteredLessons.length;

                        const subProcessTitleMatch = subProcess.title.search(regExQuery) !== -1;

                        return subProcessTitleMatch || filteredLessons.length > 0;
                    });
                    process.x_sub_processes = filteredSubProcesses;
                    process.totalLessonsCount = totalLessonsCount;

                    return processTitleMatch || filteredSubProcesses.length > 0;
                } else {
                    return processTitleMatch;
                }
            })
            .filter((process: XProcess) => {
                if (this.selectedTag && this.selectedTag.id !== 0) {
                    let totalLessonsCount = 0;
                    let hasTaggedLessons = false;
                    process.x_sub_processes.forEach((subProcess: XSubProcess) => {
                        subProcess.x_lessons = this.filterLessonsByTag(subProcess.x_lessons);
                        subProcess.x_lessons_count = subProcess.x_lessons.length;
                        totalLessonsCount += subProcess.x_lessons.length;
                        if (subProcess.x_lessons.length > 0) {
                            hasTaggedLessons = true;
                        }
                    });
                    process.totalLessonsCount = totalLessonsCount;
                    return hasTaggedLessons;
                }
                return true;
            });

        this.isSearching = false;
    }

    private filterLessonsByTag(lessons: XLesson[]): XLesson[] {
        const selectedTag = this.selectedTag;
        if (!selectedTag || selectedTag.id === 0) {
            return lessons;
        }

        return lessons.filter((lesson: XLesson) => {
            if (lesson.sub_lessons && lesson.sub_lessons.length > 0) {
                lesson.sub_lessons = this.filterLessonsByTag(lesson.sub_lessons);
            }

            const hasTag = lesson.tags ? lesson.tags.some((tag) => tag.id === selectedTag.id) : false;
            return hasTag || (lesson.sub_lessons && lesson.sub_lessons.length > 0);
        });
    }

    private prepareData() {
        this.filteredPhases = JSON.parse(JSON.stringify(this.originalProcesses));

        if (this.$route.query.searchQuery && this.$route.query.searchQuery !== '') {
            this.$store.commit(
                'knowledge/setXSearchString',
                decodeURIComponent(this.$route.query.searchQuery.toString())
            );
            if (this.$route.query.filterByLessons) {
                this.filterByLessons = this.$route.query.filterByLessons === 'true';
            }
        }
        this.search();
    }

    private getLessonsCount() {
        xLessonWebservice
            .count()
            .then((response) => {
                this.lessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getPersonalLessonsCount() {
        xLessonWebservice
            .countPersonal()
            .then((response) => {
                this.personalLessonsCount = response.data;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private toggleStoreUpdateLessonModal(xProcess: XProcess, xSubProcess: XSubProcess, xLesson: XLesson) {
        if (xSubProcess) {
            this.selectedPhase = xProcess;
            this.selectedSubPhase = xSubProcess;
        }
        this.selectedLesson = xLesson;
        this.isStoreUpdateLessonModalActive = !this.isStoreUpdateLessonModalActive;
    }

    private rowClicked(row) {
        if (hasAnyPermission(groupedXLessonPermissions)) {
            this.$router.push({
                name: 'GroupedXLessonsList',
                query: {
                    phaseId: row.id,
                    searchQuery: encodeURIComponent(this.searchQuery),
                    filterByLessons: this.filterByLessons.toString(),
                    selectedTagId: this.selectedTag && this.selectedTag.id ? this.selectedTag.id.toString() : 'all',
                },
            });
        }
    }

    private clearSearch() {
        this.$store.commit('knowledge/clearSearchStrings');
    }
}
